import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import _bindInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/bind';
import mixpanel from 'mixpanel-browser';

const _excluded = ["userId"];
let instanceCounter = 0;
class EventTracking {
  /**
   * Create an instance of the EventTracking class which you can use to track events and user actions.
   *
   * @param token - The Mixpanel project token
   * @param options - Customize the way events are tracked
   *
   * @example
   * ```javascript
   * import EventTracking from '@thisisbud/event-tracking'
   *
   * const et = new EventTracking('1fa2584e-e52c-4af4-8fb9-9841bd0c9d4e')
   * ```
   */
  constructor(token, options = {}) {
    var _options$trackUnident, _context;
    _defineProperty(this, "type", 'browser');
    _defineProperty(this, "userIdentified", false);
    _defineProperty(this, "getBasePayload", void 0);
    _defineProperty(this, "tracker", void 0);
    _defineProperty(this, "trackUnidentifiedUserEvents", void 0);
    const {
      debug = false
    } = options;
    instanceCounter++;
    this.tracker = mixpanel.init(token, {
      autotrack: false,
      batch_flush_interval_ms: 50,
      debug: debug,
      disable_cookie: true,
      disable_persistence: true,
      ip: true
    }, `bud${instanceCounter}`);
    this.getBasePayload = options.getBasePayload;
    this.trackUnidentifiedUserEvents = (_options$trackUnident = options.trackUnidentifiedUserEvents) !== null && _options$trackUnident !== void 0 ? _options$trackUnident : true;
    this.track = _bindInstanceProperty(_context = this.track).call(_context, this);
  }
  /**
   * Create an alias from one unique identifier to another.
   *
   * @param alias - A unique identifier that you want to use for this user in the future
   * @param currentId - The current identifier being used
   * @remarks If currentId is not passed, the current distinct identifier will be used
   * @public
   *
   * @example
   * ```javascript
   * import EventTracking from '@thisisbud/event-tracking'
   *
   * const et = new EventTracking('1fa2584e-e52c-4af4-8fb9-9841bd0c9d4e')
   *
   * window.addEventListener('load', function () {
   *   et.alias('known-user-id', 'current-id')
   *   // ...
   * })
   * ```
   */
  setAlias(alias, currentId) {
    if (typeof currentId !== 'string' && !this.trackUnidentifiedUserEvents) {
      return;
    }
    const tracker = this.tracker;
    tracker.alias(alias, currentId !== null && currentId !== void 0 ? currentId : tracker.get_distinct_id());
  }
  /**
   * Track an event or user action with optional additional details.
   *
   * @param eventName - The name or key identifier of the event being tracked
   * @param payload - Additional details to be sent along with the event
   * @param options - Options to control the event tracking behavior
   * @public
   *
   * @example
   * ```javascript
   * import EventTracking from '@thisisbud/event-tracking'
   *
   * const et = new EventTracking('1fa2584e-e52c-4af4-8fb9-9841bd0c9d4e')
   *
   * window.addEventListener('load', function () {
   *   et.track('page-view', { location: 'home' })
   * })
   * ```
   */
  track(eventName, payload, options = {}) {
    const {
      batch,
      callback
    } = options;
    const tracker = this.tracker;
    const getBasePayload = this.getBasePayload;
    const currentUserId = tracker.get_distinct_id();
    const basePayload = typeof getBasePayload === 'function' ? getBasePayload() : undefined;
    const _basePayload$payload = _objectSpread(_objectSpread({}, basePayload), payload),
      {
        userId
      } = _basePayload$payload,
      combinedPayload = _objectWithoutProperties(_basePayload$payload, _excluded);
    // Handle the unique identifier of the current user
    if (typeof userId === 'string') {
      // A guest user has started a session and is now a known user
      if (userId !== currentUserId) {
        this.userIdentified = true;
        tracker.identify(userId);
      }
      // A known user has ended their session and is now a guest or a different user
    } else if (this.userIdentified) {
      this.userIdentified = false;
      tracker.reset();
    }
    if (typeof userId !== 'string' && !this.trackUnidentifiedUserEvents) {
      return;
    }
    tracker.track(eventName, combinedPayload, {
      send_immediately: batch === false // eslint-disable-line @typescript-eslint/naming-convention
    }, function trackCallback() {
      if (typeof callback === 'function') {
        callback();
      }
    });
  }
}

export { EventTracking as default };
